import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { BoltApiClient, createBoltApiClient } from "./BoltApiClient";

/**
 * The shape of our React context. Here we only store the client, but you could
 * store more related items if needed in the future.
 */
interface BoltApiClientContextValue {
  boltApiClient: BoltApiClient;
}

/**
 * Create the BoltApiClientContext with an empty default. We'll throw an error
 * if it's used outside the provider, so no default object is needed here.
 */
const BoltApiClientContext = createContext<
  BoltApiClientContextValue | undefined
>(undefined);

interface BoltApiClientProviderProps {
  /**
   * The base URL or domain used for all requests.
   * May include a path or query initially, but they will be stripped.
   */
  baseUrl: string;

  /**
   * An optional object of key/value pairs to set as headers on every request.
   */
  getHeaders?: Promise<Record<string, string>>;
}

/**
 * Provides a BoltApiClient instance to any descendants that call useBoltApiClient().
 * This ensures there's exactly one shared instance within this part of the component tree.
 */
export function BoltApiClientProvider({
  baseUrl,
  getHeaders,
  children,
}: PropsWithChildren<BoltApiClientProviderProps>) {
  const [resolvedHeaders, setHeaders] = useState<Record<string, string>>();

  useEffect(() => {
    if (getHeaders) {
      getHeaders.then(setHeaders);
    }
  }, [getHeaders]);
  /**
   * We useMemo so that the client is only created once per baseUrl/headers change,
   * instead of on every render.
   */
  const boltApiClient = useMemo(() => {
    return createBoltApiClient(
      baseUrl,
      resolvedHeaders,
      () => getHeaders ?? Promise.resolve({})
    );
  }, [baseUrl, resolvedHeaders]);

  return (
    <BoltApiClientContext.Provider value={{ boltApiClient }}>
      {children}
    </BoltApiClientContext.Provider>
  );
}

/**
 * Custom hook that returns the current BoltApiClient instance from context.
 * Usage:
 *   const boltApiClient = useBoltApiClient();
 *
 * Make sure the hook is used within a <BoltApiClientProvider>.
 */
export function useBoltApiClient(): BoltApiClient {
  const contextValue = useContext(BoltApiClientContext);

  if (!contextValue) {
    throw new Error(
      "useBoltApiClient must be called from within a <BoltApiClientProvider>."
    );
  }

  return contextValue.boltApiClient;
}
